import * as globals from '@wandb/weave/common/css/globals.styles';
import {Editable} from 'slate-react';
import styled, {createGlobalStyle, css} from 'styled-components';

import {MAIN_CONTENT_WIDTH_CSS} from '../../../aria';
import {type ReportWidthOption} from '../../../state/views/report/types';
import * as BlockQuoteStyles from '../plugins/block-quotes.styles';
import * as CalloutBlockStyles from '../plugins/callout-blocks.styles';
import * as CaptivateStyles from '../plugins/captivate.styles';
import * as CodeBlockStyles from '../plugins/code-blocks.styles';
import * as CraiyonStyles from '../plugins/craiyon.styles';
import * as CTABannerStyles from '../plugins/cta-banner.styles';
import {DragHandle} from '../plugins/drag-drop/drag-drop.styles';
import * as GalleryStyles from '../plugins/gallery.styles';
import * as HeadingStyles from '../plugins/headings.styles';
import * as HorizontalRuleStyles from '../plugins/horizontal-rules.styles';
import * as ImageStyles from '../plugins/images.styles';
import * as LatexStyles from '../plugins/latex.styles';
import * as ListStyles from '../plugins/lists.styles';
import * as MarkdownBlockStyles from '../plugins/markdown-blocks.styles';
import * as PanelGridStyles from '../plugins/panel-grid/PanelGridElement.styles';
import * as ParagraphStyles from '../plugins/paragraphs.styles';
import * as ReplitStyles from '../plugins/replit.styles';
import * as TableOfContentsStyles from '../plugins/table-of-contents.styles';
import * as TableStyles from '../plugins/tables.styles';
import * as VideoStyles from '../plugins/videos.styles';
import * as WeavePanelStyles from '../plugins/weave-panels.styles';

export const Leaf = styled.span<{
  strong?: boolean;
  emphasis?: boolean;
  inlineCode?: boolean;
  underline?: boolean;
  delete?: boolean;

  codeComment?: boolean;
  operator?: boolean;
  url?: boolean;
  keyword?: boolean;
  variable?: boolean;
  regex?: boolean;
  number?: boolean;
  boolean?: boolean;
  tag?: boolean;
  constant?: boolean;
  symbol?: boolean;
  attr?: boolean;
  selector?: boolean;
  punctuation?: boolean;
  string?: boolean;
  char?: boolean;
  function?: boolean;
  isClass?: boolean;
}>`
  ${props => props.strong && 'font-weight: 600'};
  ${props => props.emphasis && 'font-style: italic'};
  text-decoration: ${props => (props.underline ? 'underline' : '')}
    ${props => (props.delete ? 'line-through' : '')};
  ${props =>
    props.inlineCode &&
    css`
      background: ${globals.THEME.bgSurfaceSunken};
      border-radius: 2px;
      border: 1px solid ${globals.THEME.border};
      font-family: Inconsolata;
    `};
  ${props =>
    props.codeComment &&
    css`
      color: light-dark(slategray, #8b949e);
    `}
  ${props =>
    (props.operator || props.url) &&
    css`
      color: light-dark(#9a6e3a, #d2a679);
    `}
  ${props =>
    props.keyword &&
    css`
      color: light-dark(#07a, #79c0ff);
    `}
  ${props =>
    (props.variable || props.regex) &&
    css`
      color: light-dark(#e90, #ffa657);
    `}
  ${props =>
    (props.number ||
      props.boolean ||
      props.tag ||
      props.constant ||
      props.symbol ||
      props.attr ||
      props.selector) &&
    css`
      color: light-dark(#905, #ff7b72);
    `}
  ${props =>
    props.punctuation &&
    css`
      color: light-dark(#999, #8b949e);
    `}
  ${props =>
    (props.string || props.char) &&
    css`
      color: light-dark(#690, #7ee787);
    `}
  ${props =>
    (props.function || props.isClass) &&
    css`
      color: light-dark(#dd4a68, #ffa198);
    `}
`;

export const SlateWrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 100%;
  span::selection {
    /* adds gold color highlight when user selects editable report content */
    background: light-dark(
      color-mix(in srgb, var(--gold-300) 48%, transparent),
      color-mix(in srgb, var(--gold-700) 48%, transparent)
    );
  }
`;

const narrowElements = `${ParagraphStyles.Paragraph}, ${HeadingStyles.HeadingOne}, ${HeadingStyles.HeadingTwo}, ${HeadingStyles.HeadingThree}, ${HeadingStyles.HeadingFour}, ${ListStyles.ListItemWrapper}, ${CodeBlockStyles.CodeBlockWrapper}, ${CalloutBlockStyles.CalloutBlock}, ${BlockQuoteStyles.BlockQuote}, ${ImageStyles.ImagePlaceholderWrapper}, ${ImageStyles.ImageOuterWrapper}, ${TableStyles.TableWrapper}, ${MarkdownBlockStyles.MarkdownBlockWrapper}, ${HorizontalRuleStyles.HorizontalRule},  ${TableOfContentsStyles.TableOfContents}, .run-sets-wrapper:not(.active), ${LatexStyles.BlockLatexWrapper}, ${WeavePanelStyles.WeavePanelWrapper}, ${CaptivateStyles.EmbeddedIframe}`;
const wideElements = `${PanelGridStyles.PanelGridWrapper}, ${VideoStyles.EmbeddedIframe}, ${ReplitStyles.EmbeddedIframe}, ${CraiyonStyles.Container}, ${GalleryStyles.Container}, ${CTABannerStyles.Container}`;
// Match the report header's horizontal padding while accounting for space
// reserved by the docked ARIA drawer.
const reportBlockWidth = `var(--wandb-report-block-width, calc(${MAIN_CONTENT_WIDTH_CSS} - 128px))`;
// TODO: Refactor this to use flex or grid layout instead of calc.
// See https://www.notion.so/wandbai/height-calc-100vh-60px-is-cursed-345e2f5c7ef3804795cfd6e62fb2f041
// oxlint-disable-next-line wandb/no-calc-100vh-pixel-math
const mobileReportBlockWidth = `var(--wandb-report-block-width, min(calc(100vw - 32px), 1072px))`;

export const StyledEditable = styled(Editable)<{
  readOnly: boolean;
  $widthMode: ReportWidthOption;
}>`
  &:focus {
    outline: 0;
  }

  ${narrowElements}, ${wideElements} {
    margin-left: auto;
    margin-right: auto;
    width: ${reportBlockWidth};
  }
  > div {
    ${DragHandle} {
      display: none;
    }
    &:hover {
      ${DragHandle} {
        display: block;
      }
    }
  }
  > ul,
  > ol {
    ${DragHandle} {
      display: none;
    }
    > div {
      &:hover {
        ${DragHandle} {
          display: block;
        }
      }
    }
  }

  ${props =>
    props.$widthMode === 'profile' &&
    css`
      ${narrowElements}, ${wideElements} {
        width: ${props.readOnly ? `100%` : `min(${reportBlockWidth}, 854px)`};
      }

      // Hack to eliminate extra space at the top
      &
      > ${ParagraphStyles.Paragraph}:first-child
      + ${HeadingStyles.HeadingOne},
      &
      > ${ParagraphStyles.Paragraph}:first-child
      + ${HeadingStyles.HeadingTwo},
      &
      > ${ParagraphStyles.Paragraph}:first-child
      + ${HeadingStyles.HeadingThree},
      &
      > ${ParagraphStyles.Paragraph}:first-child
      + ${HeadingStyles.HeadingFour} {
        margin-top: 0 !important;
      }
    `}

  ${props =>
    props.$widthMode === 'readable' &&
    css`
      ${narrowElements} {
        width: min(${reportBlockWidth}, 716px);
      }
      ${wideElements} {
        width: min(${reportBlockWidth}, 716px);
      }
    `}

  ${props =>
    props.$widthMode === 'fixed' &&
    css`
      ${narrowElements}, ${wideElements} {
        width: min(${reportBlockWidth}, 1072px);
      }
    `}

  @media only screen and (max-width: 768px) {
    ${narrowElements}, ${wideElements} {
      width: ${mobileReportBlockWidth};
    }
  }

  // Hack to make the trailing paragraph more clickable
  & > ${ParagraphStyles.Paragraph}:last-child {
    height: 300px;
  }
`;

export const PrintReportHack = createGlobalStyle`
  .report-page.report-width-print-portrait {
    ${narrowElements}, ${wideElements} {
      width: calc(8.5in - 256px);
    }
  }
  .report-page.report-width-print-landscape {
    ${narrowElements}, ${wideElements} {
      width: calc(11in - 256px);
    }
  }
`;
